import { FC, useRef, useState } from 'react';
import SelectGroupScreen from './select-group-screen';
import SelectInterestsScreen from './select-interests-screen';
import {
  useInterestsQuery,
  useSelectInterestsMutation,
} from 'graphql/__generated__/graphql';
import withApolloProvider from 'graphql/with-provider';
import {
  saveNextCareNowLocally,
  getNextCareNowLocally,
} from 'pages/home/helpers/your-program-helpers';
import { useInterestsUpdatedHook } from '../hook';

type Props = {
  onFinish: () => void;
};

const SelectInterestsContainer: FC<Props> = ({ onFinish }: Props) => {
  const { data, loading } = useInterestsQuery({
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true, // sets loading back to true when refetch()
  });

  const [selectInterests, { loading: mutationLoading }] =
    useSelectInterestsMutation();

  const { notifyUpdate } = useInterestsUpdatedHook();

  const [showInterests, setShowInterests] = useState(true);

  const selectedInterests = useRef<{
    interests: string[];
    groups: string[];
  }>({
    interests: [],
    groups: [],
  });

  const onSubmitInterests = (interests: string[], selectedGroups: string[]) => {
    selectedInterests.current.interests = interests;
    selectedInterests.current.groups = selectedGroups;

    if (selectedGroups.length === 1) {
      return onSubmit(selectedGroups[0] ?? '', interests);
    }

    setShowInterests(false);
  };

  const onSubmit = async (group: string, selectedInterests: string[]) => {
    const savedCareNow = getNextCareNowLocally();

    const { data } = await selectInterests({
      variables: {
        input: {
          group,
          interests: selectedInterests,
        },
      },
    });

    if (!savedCareNow) {
      saveNextCareNowLocally({
        ...data?.selectInterests?.immediateRecommendation?.payload,
        isRecommended: true,
      });

      notifyUpdate();
    }

    onFinish();
  };

  const onSubmitGroup = (group: string) => {
    onSubmit(group, selectedInterests.current.interests);
  };

  const getSelectedGroups = () => {
    const selectedGroups = data?.interests?.groups?.filter(group =>
      selectedInterests.current.groups.includes(group.id),
    );

    return selectedGroups ?? [];
  };

  return showInterests ? (
    <SelectInterestsScreen
      isLoading={loading || mutationLoading}
      interests={data?.interests?.groups}
      onContinue={onSubmitInterests}
    />
  ) : (
    <SelectGroupScreen
      isLoading={mutationLoading}
      groups={getSelectedGroups()}
      onContinue={onSubmitGroup}
    />
  );
};

export default withApolloProvider(SelectInterestsContainer);
